import { AuthProvider, baseHeaders } from '@descope/react-sdk';
import { env } from 'app/Env/env';
import { QueryParam, StorageKey } from 'consts/keys';
import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useLocation } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';

// Override baseHeaders
Object.assign(baseHeaders, {
	'x-descope-sdk-name': 'admin-portal',
	'x-descope-sdk-version': APP_VERSION || 'unknown'
});

interface IProjectCtxValue {
	projectId: string | null;
	cookieName: string;
	initialized: boolean;
}

const ProjectCtx = createContext<IProjectCtxValue | undefined>(undefined);

interface IProjectCtxProviderProps {
	children: React.ReactNode;
	initialProjectId?: string | null;
}

export const ProjectCtxProvider: React.FC<IProjectCtxProviderProps> = ({
	children,
	initialProjectId = null
}) => {
	const [projectId, setProjectId] = useState<string | null>(initialProjectId);
	const [initialized, setInitialized] = useState<boolean>(!!initialProjectId);
	const [cookieName, setCookieName] = useState<string>('');

	const value = useMemo(
		() => ({ projectId, cookieName, initialized }),
		[projectId, cookieName, initialized]
	);

	return (
		<ProjectCtx.Provider value={value}>
			{projectId === null && (
				<>
					{/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
					<ProjectCtxInitializer
						projectId={projectId}
						setProjectId={setProjectId}
						setInitialized={setInitialized}
						setCookieName={setCookieName}
					/>
					{children}
				</>
			)}
			{projectId && (
				<AuthProvider
					projectId={projectId}
					baseStaticUrl={env.baseStaticUrl || undefined}
					baseUrl={env.baseDescopeUrl || undefined}
					refreshCookieName={cookieName || undefined}
				>
					{children}
				</AuthProvider>
			)}
		</ProjectCtx.Provider>
	);
};

export const useProjectCtx = (): IProjectCtxValue => {
	const context = useContext(ProjectCtx);
	if (!context) throw new Error('ProjectCtxProvider is missing');
	return context;
};

const ProjectCtxInitializer: React.FC<{
	projectId: string | null;
	setProjectId: React.Dispatch<React.SetStateAction<string | null>>;
	setInitialized: React.Dispatch<React.SetStateAction<boolean>>;
	setCookieName: React.Dispatch<React.SetStateAction<string>>;
}> = ({ projectId, setProjectId, setInitialized, setCookieName }) => {
	const { pathname } = useLocation();
	const [urlCookieName] = useQueryParams(QueryParam.CookieName);
	const parts = pathname.split('/P');
	let pathProjectId = '';
	if (parts.length > 1) {
		// remove trailing /
		pathProjectId = `P${parts[1].split('/')[0]}`;
	}

	useEffect(() => {
		if (!projectId) {
			const storageProjectId = localStorage.getItem(StorageKey.ProjectId);
			setProjectId(pathProjectId || storageProjectId || null);
			localStorage.setItem(
				StorageKey.ProjectId,
				pathProjectId || storageProjectId || ''
			);
			setInitialized(true);
		}

		const resolvedCookieName =
			urlCookieName ||
			env.cookieName ||
			localStorage.getItem(StorageKey.CookieName) ||
			'';

		setCookieName(resolvedCookieName);

		if (resolvedCookieName)
			localStorage.setItem(StorageKey.CookieName, resolvedCookieName);
	}, [
		pathProjectId,
		projectId,
		urlCookieName,
		setProjectId,
		setInitialized,
		setCookieName
	]);
	return null;
};
